/**
 * Kommodo Screen Recorder & SOP
 *
 * Exposes three broad WAR groups — `static`, `dist/pages/cs-iframes` and
 * `dist/cs/ui-variants` (all globbed) — to every origin with no `use_dynamic_url`, so
 * every bundled file under them is fetchable from any page at a fixed URL.
 *
 * The vendor logo set under `static/images/logo/` is the cleanest signal: the files are
 * stable, vendor-named, and — being images — load through `probeImage` with no CORS
 * grant, so they survive a restrictive page CSP.
 *
 * Manifest verified against version 3.3.2.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kodipildlgjdkoeokgibdlighekmebcg';

export const kommodo: ExtensionDefinition = {
  id: ID,
  name: 'Kommodo Screen Recorder & SOP',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 50_000,
  notes:
    'Exposes static/ and dist/ globs to every origin with no dynamic URL. The vendor-named ' +
    'logo images are conclusive and load without a CORS grant.',
  signals: [
    {
      id: 'war-logo-original-png',
      kind: 'web-accessible-resource',
      description:
        'Load static/images/logo/logo-original.png via <img> — a vendor-named logo ' +
        'exposed to every origin; needs no CORS grant, so it works under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'static/images/logo/logo-original.png'),
    },
    {
      id: 'war-symbol-original-png',
      kind: 'web-accessible-resource',
      description:
        'Load static/images/logo/symbol-original.png via <img> — a second vendor-named ' +
        'icon, corroborating the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'static/images/logo/symbol-original.png'),
    },
    {
      id: 'war-avatar-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch dist/pages/cs-iframes/avatar/avatar.html — a content-script iframe entry ' +
        'point, independent of the logo images.',
      weight: 0.5,
      run: () => probeFetch(ID, 'dist/pages/cs-iframes/avatar/avatar.html'),
    },
  ],
};
